
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import {
  ColumnDefinition,
  getFilterParamAsNumberArray,
  getFilterParamAsStringArray,
  getFilterParams,
  OptionType,
  useDataTable,
  useSelectOptionsResource,
  AdvancedSearch,
  DataTable,
  BaseButton,
  EditIcon,
  DeleteIcon,
  FormFieldMultiSelect,
  OpenInBrowserIcon,
  ContentCopyIcon,
} from "@tager/admin-ui";
import { Page } from "@tager/admin-layout";
import {
  FETCH_STATUSES,
  isNotNullish,
  notEmpty,
  useResourceClone,
  useResourceDelete,
} from "@tager/admin-services";
import { getNameWithDepth } from "@tager/admin-services";

import {
  cloneProduct,
  deleteProduct,
  getBrandList,
  getCategoryList,
  getCollectionList,
  getProductGroupList,
  getProductList,
  getProductTypeList,
  getSpecialList,
} from "@/services/requests";
import {
  BrandType,
  CategoryType,
  CollectionType,
  ProductGroupType,
  ProductType,
  ProductTypeType,
  SpecialType,
} from "@/typings/model";
import {
  getFieldListUrl,
  getProductListUrl,
  getProductFormUrl,
} from "@/utils/paths";
import { getWebsiteOrigin } from "@/utils/common";
import PriorityBlock from "@/views/ProductList/components/PriorityBlock.vue";

const COLUMN_DEFS: Array<ColumnDefinition<ProductType>> = [
  {
    id: 1,
    name: "ID",
    field: "id",
    style: { width: "50px", textAlign: "center" },
    headStyle: { width: "50px", textAlign: "center" },
  },
  {
    id: 2,
    name: "Приоритет",
    field: "priority",
    style: { width: "100px" },
    headStyle: { width: "100px" },
  },
  {
    id: 3,
    name: "Картинка",
    field: "image",
    type: "image",
    style: { width: "100px" },
    headStyle: { width: "100px" },
  },
  {
    id: 4,
    name: "Название",
    field: "title",
    type: "name",
    format: ({ row }) => ({
      adminLink: {
        text: row.title,
        url: getProductFormUrl({ productId: row.id }),
        subtext: row.subtitle ?? undefined,
      },
      websiteLink: {
        text: row.url,
        url: row.url,
      },
      paramList: [
        row.code ? { name: "Код товара", value: row.code } : null,
        row.productTypeName
          ? { name: "Тип товара", value: row.productTypeName }
          : null,
        row.brand ? { name: "Бренд", value: row.brand } : null,
        row.categoryName
          ? { name: "Базовая категория", value: row.categoryName }
          : null,
        { name: "Штрих-код", value: row.barcode ?? "отсутствует" },
      ].filter(isNotNullish),
    }),
  },
  {
    id: 7,
    name: "Цена",
    field: "price",
    style: { whiteSpace: "nowrap", width: "131px" },
    headStyle: { width: "131px" },
  },
  {
    id: 8,
    name: "Наличие",
    field: "availability",
    style: { whiteSpace: "nowrap", width: "153px" },
    headStyle: { width: "153px" },
  },
  {
    id: 9,
    name: "Действия",
    field: "actions",
    style: { width: "140px", textAlign: "center", whiteSpace: "nowrap" },
    headStyle: { width: "140px", textAlign: "center" },
  },
];

const availabilityOptionList = [
  { value: "AVAILABLE", label: "В наличии" },
  { value: "ON_REQUEST", label: "Под заказ" },
  { value: "PRE-ORDER", label: "Скоро в продаже" },
  { value: "OUT_OF_STOCK", label: "Нет в наличии" },
];

interface TagType {
  value: string;
  label: string;
  name: string;
  title: string;
}

export default defineComponent({
  name: "ProductList",
  components: {
    Page,
    PriorityBlock,
    AdvancedSearch,
    DataTable,
    BaseButton,
    EditIcon,
    DeleteIcon,
    FormFieldMultiSelect,
    OpenInBrowserIcon,
    ContentCopyIcon,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    /** Get Filters Start **/
    const {
      loading: isCategoryListLoading,
      options: categoryOptionList,
      fetchEntityList: fetchCategoryList,
      status: categoryListRequestStatus,
    } = useSelectOptionsResource<CategoryType, OptionType<number>>({
      fetchEntityList: () => getCategoryList({ pageNumber: 1, pageSize: 9999 }),
      resourceName: "Category list",
      minQueryLength: 0,
      convertEntityToOption: (category) => {
        return {
          value: category.id,
          label: getNameWithDepth(category.name, category.depth),
        };
      },
    });

    const {
      loading: isSpecialListLoading,
      options: specialOptionList,
      fetchEntityList: fetchSpecialList,
      status: specialListRequestStatus,
    } = useSelectOptionsResource<SpecialType, OptionType<number>>({
      fetchEntityList: () => getSpecialList({ pageNumber: 1, pageSize: 9999 }),
      resourceName: "Special list",
      minQueryLength: 0,
      convertEntityToOption: (special) => {
        return {
          value: special.id,
          label: special.name,
        };
      },
    });

    const {
      loading: isProductGroupListLoading,
      options: productGroupOptionList,
      fetchEntityList: fetchProductGroupList,
      status: productGroupListRequestStatus,
    } = useSelectOptionsResource<ProductGroupType, OptionType<number>>({
      fetchEntityList: () =>
        getProductGroupList({ pageNumber: 1, pageSize: 9999 }),
      resourceName: "Product group list",
      minQueryLength: 0,
      convertEntityToOption: (productGroup) => {
        return {
          value: productGroup.id,
          label: productGroup.name,
        };
      },
    });

    const {
      loading: isCollectionListLoading,
      options: collectionOptionList,
      fetchEntityList: fetchCollectionList,
      status: collectionListRequestStatus,
    } = useSelectOptionsResource<CollectionType, OptionType<number>>({
      fetchEntityList: () =>
        getCollectionList({ pageNumber: 1, pageSize: 9999 }),
      resourceName: "Collection list",
      minQueryLength: 0,
      convertEntityToOption: (collection) => {
        return {
          value: collection.id,
          label: collection.name,
        };
      },
    });

    const {
      loading: isBrandListLoading,
      options: brandOptionList,
      fetchEntityList: fetchBrandList,
      status: brandListRequestStatus,
    } = useSelectOptionsResource<BrandType, OptionType<number>>({
      fetchEntityList: () => getBrandList({ pageNumber: 1, pageSize: 9999 }),
      resourceName: "Brand list",
      minQueryLength: 0,
      convertEntityToOption: (brand) => {
        return {
          value: brand.id,
          label: brand.name,
        };
      },
    });

    const {
      loading: isProductTypeListLoading,
      options: productTypeOptionList,
      fetchEntityList: fetchProductTypeList,
      status: productTypeListRequestStatus,
    } = useSelectOptionsResource<ProductTypeType, OptionType<number>>({
      fetchEntityList: () =>
        getProductTypeList({ pageNumber: 1, pageSize: 9999 }),
      resourceName: "ProductType list",
      minQueryLength: 0,
      convertEntityToOption: (productType) => {
        return {
          value: productType.id,
          label: productType.name,
        };
      },
    });

    onMounted(() => {
      fetchCategoryList({ query: "" });
      fetchSpecialList({ query: "" });
      fetchProductGroupList({ query: "" });
      fetchCollectionList({ query: "" });
      fetchBrandList({ query: "" });
      fetchProductTypeList({ query: "" });
    });

    const initialCategoryFilter = computed(() => {
      const queryValue = getFilterParamAsNumberArray(route.query, "category");
      return categoryOptionList.value.filter((option) =>
        queryValue.some((selected) => option.value === selected)
      );
    });

    const categoryFilter = ref<Array<OptionType<number>>>(
      initialCategoryFilter.value
    );

    watch(initialCategoryFilter, () => {
      categoryFilter.value = initialCategoryFilter.value;
    });

    const initialBrandFilter = computed(() => {
      const queryValue = getFilterParamAsNumberArray(route.query, "brand");

      return brandOptionList.value.filter((option) =>
        queryValue.some((selected) => option.value === selected)
      );
    });

    const brandFilter = ref<Array<OptionType<number>>>(
      initialBrandFilter.value
    );

    watch(initialBrandFilter, () => {
      brandFilter.value = initialBrandFilter.value;
    });

    const initialProductTypeFilter = computed(() => {
      const queryValue = getFilterParamAsNumberArray(
        route.query,
        "productType"
      );

      return productTypeOptionList.value.filter((option) =>
        queryValue.some((selected) => option.value === selected)
      );
    });

    const productTypeFilter = ref<Array<OptionType<number>>>(
      initialProductTypeFilter.value
    );

    watch(initialProductTypeFilter, () => {
      productTypeFilter.value = initialProductTypeFilter.value;
    });

    const initialAvailabilityFilter = computed<Array<OptionType>>(() => {
      const queryValue = getFilterParamAsStringArray(
        route.query,
        "availability"
      );

      return availabilityOptionList.filter((option) =>
        queryValue.some((selected) => option.value === selected)
      );
    });

    const availabilityFilter = ref<Array<OptionType>>(
      initialAvailabilityFilter.value
    );

    watch(initialAvailabilityFilter, () => {
      availabilityFilter.value = initialAvailabilityFilter.value;
    });

    const initialSpecialFilter = computed<Array<OptionType<number>>>(() => {
      const queryValue = getFilterParamAsNumberArray(route.query, "special");

      return specialOptionList.value.filter((option) =>
        queryValue.some((selected) => option.value === selected)
      );
    });

    const specialFilter = ref<Array<OptionType<number>>>(
      initialSpecialFilter.value
    );

    watch(initialSpecialFilter, () => {
      specialFilter.value = initialSpecialFilter.value;
    });

    const initialProductGroupFilter = computed<Array<OptionType<number>>>(
      () => {
        const queryValue = getFilterParamAsNumberArray(
          route.query,
          "productGroup"
        );

        return productGroupOptionList.value.filter((option) =>
          queryValue.some((selected) => option.value === selected)
        );
      }
    );

    const productGroupFilter = ref<Array<OptionType<number>>>(
      initialProductGroupFilter.value
    );

    watch(initialProductGroupFilter, () => {
      productGroupFilter.value = initialProductGroupFilter.value;
    });

    const initialCollectionFilter = computed<Array<OptionType<number>>>(() => {
      const queryValue = getFilterParamAsNumberArray(route.query, "collection");

      return collectionOptionList.value.filter((option) =>
        queryValue.some((selected) => option.value === selected)
      );
    });

    const collectionFilter = ref<Array<OptionType<number>>>(
      initialCollectionFilter.value
    );

    watch(initialCollectionFilter, () => {
      collectionFilter.value = initialCollectionFilter.value;
    });

    const filterParams = computed(() => {
      return getFilterParams({
        category: categoryFilter.value.map((tag) => tag.value),
        brand: brandFilter.value.map((tag) => tag.value),
        productType: productTypeFilter.value.map((tag) => tag.value),
        availability: availabilityFilter.value.map((tag) => tag.value),
        special: specialFilter.value.map((tag) => tag.value),
        productGroup: productGroupFilter.value.map((tag) => tag.value),
        collection: collectionFilter.value.map((tag) => tag.value),
      });
    });

    const isAllFilterDataLoaded = computed(() => {
      return [
        categoryListRequestStatus.value,
        brandListRequestStatus.value,
        productTypeListRequestStatus.value,
        specialListRequestStatus.value,
        productGroupListRequestStatus.value,
        collectionListRequestStatus.value,
      ].every((status) => status === FETCH_STATUSES.SUCCESS);
    });
    /** Get Filters End **/

    const {
      isLoading: isProductListLoading,
      rowData: productList,
      errorMessage,
      searchQuery,
      handleChange,
      pageNumber,
      pageCount,
      pageSize,
      fetchEntityList: fetchProductList,
    } = useDataTable<ProductType>({
      fetchEntityList: (params) =>
        getProductList({
          query: params.searchQuery,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          ...filterParams.value,
        }),
      initialValue: [],
      resourceName: "Product list",
    });

    watch(filterParams, () => {
      if (!isAllFilterDataLoaded.value) return;

      router.replace({
        query: {
          ...filterParams.value,
          query: (route.query.query ?? "") as string,
        },
      });

      fetchProductList();
    });

    const { isCloning, handleResourceClone } = useResourceClone({
      cloneResource: cloneProduct,
      confirmMessage: "Вы уверены, что хотите клонировать товар?",
      successMessage: "Товар успешно склонирован",
      failureMessage: "Ошибка клонирования товара",
      onSuccessRedirectTo: (data: ProductType) =>
        "/products/" + data.id + "/edit",
    });

    const { handleResourceDelete: handleProductDelete, isDeleting } =
      useResourceDelete({
        deleteResource: deleteProduct,
        resourceName: "Product",
        onSuccess: fetchProductList,
      });

    const isRowDataLoading = computed<boolean>(
      () =>
        isProductListLoading.value ||
        isCategoryListLoading.value ||
        isBrandListLoading.value ||
        isSpecialListLoading.value ||
        isProductGroupListLoading.value ||
        isCollectionListLoading.value ||
        isProductTypeListLoading.value
    );

    function isBusy(productId: number): boolean {
      return (
        isDeleting(productId) || isCloning(productId) || isRowDataLoading.value
      );
    }

    function getLabelVacancies(countPrice: number): string {
      if (countPrice % 10 == 1 && countPrice % 100 !== 11) {
        return countPrice + " позиция";
      }
      if (
        (countPrice % 10 == 2 || countPrice % 10 === 3) &&
        countPrice % 100 !== 12 &&
        countPrice % 100 !== 13
      ) {
        return countPrice + " позиции";
      }
      return countPrice + " позиций";
    }

    function getAvailabilityLabel(
      availability: string,
      days: number | null,
      quantity: number | null
    ): string {
      if (availability === "OUT_OF_ORDER") {
        return "Нет в наличии";
      }
      if (availability === "PRE_ORDER") {
        return "Скоро в продаже";
      }
      if (availability === "ON_REQUEST") {
        if (days) {
          return `Под заказ (${days} дн.)`;
        }
        return "Под заказ";
      }
      if (availability === "AVAILABLE") {
        if (quantity) {
          return `В наличии (${quantity} шт.)`;
        }
        return "В наличии";
      }
      return "";
    }

    /** Set Filters Start **/

    function handleTagRemove(event: TagType) {
      if (event.name === "category") {
        categoryFilter.value = categoryFilter.value.filter(
          (tag) => String(tag.value) !== event.value
        );
      }
      if (event.name === "brand") {
        brandFilter.value = brandFilter.value.filter(
          (tag) => String(tag.value) !== event.value
        );
      }
      if (event.name === "productType") {
        productTypeFilter.value = productTypeFilter.value.filter(
          (tag) => String(tag.value) !== event.value
        );
      }
      if (event.name === "availability") {
        availabilityFilter.value = availabilityFilter.value.filter(
          (tag) => String(tag.value) !== event.value
        );
      }
      if (event.name === "special") {
        specialFilter.value = specialFilter.value.filter(
          (tag) => String(tag.value) !== event.value
        );
      }
      if (event.name === "productGroup") {
        productGroupFilter.value = productGroupFilter.value.filter(
          (tag) => String(tag.value) !== event.value
        );
      }
      if (event.name === "collection") {
        collectionFilter.value = collectionFilter.value.filter(
          (tag) => String(tag.value) !== event.value
        );
      }
    }

    const tags = computed<Array<TagType>>(() =>
      [
        ...categoryFilter.value.map((tag) => ({
          value: String(tag.value),
          label: tag.label,
          name: "category",
          title: "Категории",
        })),
        ...brandFilter.value.map((tag) => ({
          value: String(tag.value),
          label: tag.label,
          name: "brand",
          title: "Категории",
        })),
        ...productTypeFilter.value.map((tag) => ({
          value: String(tag.value),
          label: tag.label,
          name: "productType",
          title: "Категории",
        })),
        ...availabilityFilter.value.map((tag) => ({
          value: String(tag.value),
          label: tag.label,
          name: "availability",
          title: "Источники",
        })),
        ...specialFilter.value.map((tag) => ({
          value: String(tag.value),
          label: tag.label,
          name: "special",
          title: "Спецпредложения",
        })),
        ...productGroupFilter.value.map((tag) => ({
          value: String(tag.value),
          label: tag.label,
          name: "productGroup",
          title: "Группы товаров",
        })),
        ...collectionFilter.value.map((tag) => ({
          value: String(tag.value),
          label: tag.label,
          name: "collection",
          title: "Коллекции",
        })),
      ].filter(notEmpty)
    );

    /** Set Filters eEnd **/

    return {
      columnDefs: COLUMN_DEFS,
      rowData: productList,
      isRowDataLoading,
      errorMessage,
      isBusy,
      handleProductDelete,
      getProductFormUrl,
      getFieldListUrl,
      getProductListUrl,
      getWebsiteOrigin,
      searchQuery,
      handleChange,
      pageNumber,
      pageCount,
      pageSize,
      getLabelVacancies,
      getAvailabilityLabel,

      availabilityFilter,
      availabilityOptionList,
      productTypeFilter,
      productTypeOptionList,
      categoryFilter,
      categoryOptionList,
      brandFilter,
      brandOptionList,
      specialFilter,
      specialOptionList,
      productGroupFilter,
      productGroupOptionList,
      collectionFilter,
      collectionOptionList,

      filterParams,

      handleTagRemove,
      handleResourceClone,
      tags,
    };
  },
});
